import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import axiosInstance, { TokenManager, Logout } from '../components/axios';
import { isValidEmail, isValidMFABackupCode, isValidMFACode,  } from '../components/validation';
import Toast from '../components/toast';
import { useAuth } from '../context/auth_context';

// Static resources
import bgimage from '../images/login-page.jpg';

//MaterialUI
import Avatar from '@mui/material/Avatar';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import PinIcon from '@mui/icons-material/Pin';

export default function Login() {

	const navigate = useNavigate();
	let [searchParams] = useSearchParams();

	const { isAuthenticated, setIsAuthenticated } = useAuth();

	// Redirect authenticated users away from login
	useEffect(() => {
		if (isAuthenticated) {
			const next = searchParams.get('next') || '/list';
			navigate(next, { replace: true });
		}
	}, [isAuthenticated, searchParams, navigate]);

	// Form State
	const [loading, setLoading] = useState(false);
	const [mfaActive, setMFAActive] = useState(false);
	const [mfaUseBackupCode, setMFAUseBackupCode] = useState(false);
	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
		duration: 3000,
		clickToClose: false,
	});

	// Sign-in Form
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [remember, setRemember] = useState(false);

	// MFA Form
	const [ephemeralToken, setEphemeralToken] = useState('');
	const [mfaMethod, setMFAMethod] = useState('app');  // Currently the only option is 'app' - unused state var
	const [mfaCode, setMFACode] = useState('');
	const [mfaBackupCode, setMFABackupCode] = useState('');
	
	// Form Validity
	const [emailValid, setEmailValid] = useState(false);
	const [passwordValid, setPasswordValid] = useState(false);
	const [mfaCodeValid, setMFACodeValid] = useState(false);
	const [backupCodeValid, setBackupCodeValid] = useState(false);

	// Form Validity checks
	useEffect(() => { setEmailValid(isValidEmail(email)); }, [email]);
	
	useEffect(() => { setPasswordValid(password && password.length > 0) }, [password]);
	
	useEffect(() => { setMFACodeValid(isValidMFACode(mfaCode)) }, [mfaCode]);
	
	useEffect(() => { setBackupCodeValid(isValidMFABackupCode(mfaBackupCode)) }, [mfaBackupCode]);

	// Handle Clearing local storage
	useEffect(() => {
		let refreshTokenValid = TokenManager.isRefreshValid();
		if (!refreshTokenValid) {
			Logout();
		}
	}, []);

	// Handle actual sign-in and MFA (if active)
	function handleSignIn() {
		Logout();  // Clear any existing tokens

		if (emailValid && passwordValid) {
			setLoading(true);
			axiosInstance.post(`auth/login/`, {
					username: email,
					password: password,
				})
				.then((res) => {
					if (res.data && res.data.ephemeral_token) {
						setEphemeralToken(res.data.ephemeral_token);
						setMFAMethod(res.data.method);

						setMFAActive(true);
						setMFAUseBackupCode(false);
					} else if (res.data && res.data.access)  {
						TokenManager.save(res.data.access, res.data.refresh);
						setIsAuthenticated(true);
					} else {
						console.log('Invalid response from server:', res);
					}
				})
				.catch((error) => {
					setToast({ open: true, severity: 'error', message: 'Invalid email or password. Please try again.', duration: 10000, clickToClose: true });
					console.error('Error signing in:', error);
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			setToast({ open: true, severity: 'error', message: 'Please enter a valid email and password.', duration: 3000, clickToClose: true });
		}
	}

	function handleMFA() {
		if (!ephemeralToken) {
			setToast({ open: true, severity: 'error', message: 'MFA Access timed out. Please sign in again.', duration: 5000, clickToClose: true });
			clearForm();
			setMFAActive(false);
		} else {
			if ( (!mfaUseBackupCode && mfaCodeValid) || (mfaUseBackupCode && backupCodeValid) ) {
				let code = mfaUseBackupCode ? mfaBackupCode : mfaCode;

				setLoading(true);
				axiosInstance.post(`auth/login/code/`, {
						ephemeral_token: ephemeralToken,
						code: code,
					})
					.then((res) => {
						if (res.data && res.data.access) {
							TokenManager.save(res.data.access, res.data.refresh);
							setIsAuthenticated(true);
						} else {
							console.log('Invalid response from server:', res);
						}
					})
					.catch((error) => {
						setToast({
							open: true,
							severity: 'error',
							message: 'Please enter a valid ' + (mfaUseBackupCode ? 'Backup' : 'MFA') + ' code.',
							duration: 10000,
							clickToClose: true
						});
						console.error('Error signing in:', error);
					})
					.finally(() => {
						setLoading(false);
					});
			} else {
				setToast({
					open: true,
					severity: 'error',
					message: 'Please enter a valid ' + (mfaUseBackupCode ? 'Backup' : 'MFA') + ' code.',
					duration: 10000,
					clickToClose: true
				});
			}
		}
	}
	
	// Clear all form state
	function clearForm() {
		setEmail('');
		setPassword('');

		setEphemeralToken('');
		setMFACode('');
		setMFABackupCode('');
		
		setMFAActive(false);
		setMFAUseBackupCode(false);
	}
	
	// Sign in form submittal
	const handleSubmit = (e) => {
		e.preventDefault();

		// Handle the case where refresh_token expires while on the login page
		if (!TokenManager.isRefreshValid()) {
			Logout();
		}

		handleSignIn();
	};
	
	// MFA form submittal
	const handleMFASubmit = (e) => {
		e.preventDefault();
		handleMFA();
	};

	// Cancel button handler
	const handleCancel = (e) => {
		clearForm();
	};

	const handleMFAAltButton = (e) => {
		setMFAUseBackupCode(!mfaUseBackupCode);
	}

	const handlePassResetClick = () => {
		navigate('/passwordreset');
		// setToast({
		// 	open: true,
		// 	severity: 'info',
		// 	message: 'Please contact your administrator to reset your password if you are having trouble logging in.',
		// 	duration: 5000,
		// 	clickToClose: true
		// });
	}

	const loginForm = (
		<Box
			sx={{
				my: 8,
				mx: 4,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
			}}
		>
			<Avatar sx={{ m: 1, bgcolor: '#B1282E' }}>
				<LockOutlinedIcon />
			</Avatar>
			<Typography component='h1' variant='h5'>
				Sign in
			</Typography>
			<Box component='form' onSubmit={handleSubmit} sx={{ mt: 1 }}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<TextField
							id='email'
							name='email'
							label='Email Address'
							autoComplete='email'
							required
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							inputProps={{ maxLength: 64 }}
							error={email.length > 0 && !emailValid}
							helperText={(email && !emailValid) ? 'Invalid email address' : ''}
							variant='outlined'
							margin='normal'
							fullWidth
							autoFocus
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id='password'
							name='password'
							label='Password'
							type='password'
							autoComplete='password'
							required
							value={password}
							onChange={(event) => setPassword(event.target.value)}
							inputProps={{ maxLength: 32 }}
							variant='outlined'
							margin='normal'
							fullWidth
						/>
					</Grid>
					<Grid item xs={12}>
						<FormControlLabel
							control={<Checkbox value={remember} onChange={(event) => setRemember(event.target.checked)} color='primary' />}
							label='Remember me'
							sx={{ ml: 1 }}
						/>
					</Grid>
					<Grid item xs={12}>
						<Button
							fullWidth
							variant='contained'
							sx={{ mt: 1, mb: 2 }}
							type='submit'
						>
							Sign In
						</Button>
					</Grid>
					<Grid item xs>
						<Link onClick={handlePassResetClick} variant='body2' style={{ cursor: 'pointer' }}>
							Forgot password?
						</Link>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);

	const mfaForm = (
		<Box
			sx={{
				my: 8,
				mx: 4,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
			}}
		>
			<Avatar sx={{ m: 1, bgcolor: '#B1282E' }}>
				{ mfaUseBackupCode ? <PinIcon /> : <SecurityIcon /> }
			</Avatar>
			<Typography component='h1' variant='h5'>
				MFA Verification
			</Typography>
			<Box component='form' onSubmit={handleMFASubmit} sx={{ mt: 1 }}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						{ mfaUseBackupCode ? (
							<TextField
								id='mfaBackupCode'
								name='mfaBackupCode'
								label='MFA Backup Code'
								required
								value={mfaBackupCode}
								onChange={(event) => setMFABackupCode(event.target.value)}
								inputProps={{ maxLength: 12, autoComplete: 'new-password' }}
								error={mfaBackupCode.length > 0 && !backupCodeValid}
								helperText={(mfaBackupCode && !backupCodeValid) ? 'Invalid MFA Backup code' : ''}
								variant='outlined'
								margin='normal'
								fullWidth
								autoFocus
							/>
						) : (
							<TextField
								id='mfaCode'
								name='mfaCode'
								label='MFA Code'
								required
								value={mfaCode}
								onChange={(event) => setMFACode(event.target.value)}
								inputProps={{ maxLength: 6, autoComplete: 'new-password' }}
								error={mfaCode.length > 0 && !mfaCodeValid}
								helperText={(mfaCode && !mfaCodeValid) ? 'Invalid MFA code' : ''}
								variant='outlined'
								margin='normal'
								fullWidth
								autoFocus
							/>
						) }
					</Grid>
					<Grid item xs={6}>
						<Button
							fullWidth
							variant='contained'
							sx={{ mt: 1 }}
							type='submit'
						>
							Verify
						</Button>
					</Grid>
					<Grid item xs={6}>
						<Button
							fullWidth
							variant='outlined'
							sx={{ mt: 1 }}
							onClick={handleCancel}
						>
							Cancel
						</Button>
					</Grid>
					<Grid item xs={12}>
						<Button
							fullWidth
							variant='outlined'
							sx={{ mt: 1 }}
							onClick={handleMFAAltButton}
						>
							{ mfaUseBackupCode ? 'Use MFA Code' : 'Use Backup Code' }
						</Button>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);

	return (		
		<Grid container component='main' sx={{ height: '100vh' }}>
			<CssBaseline />
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
				duration={toast.duration}
				clickToClose={toast.clickToClose}
			/>
			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={loading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid item xs={false} sm={4} md={6} lg={8} sx={{
				backgroundImage: `url(${bgimage})`,
				backgroundRepeat: 'no-repeat',
				backgroundColor: (t) =>
				t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
				backgroundSize: 'cover',
				backgroundPosition: '5%',
			}} />
			<Grid item xs={12} sm={8} md={6} lg={4} component={Paper} elevation={6} square>
				{ mfaActive ? mfaForm : loginForm }
			</Grid>
		</Grid>
	);
}